import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@ps/core-config';
import {
  BulkStats,
  ProjectUpdatePayloadInput,
  McGroupedUpdatePayloadMapper,
  ProjectPayloadDto,
  ProjectPayloadInput,
  Survey,
} from '@ps/survey-utils';
import { map, Observable } from 'rxjs';
import { BulkStatsDto } from './dtos/bulk-edit.dto';
import { CreatedProjectDto } from './dtos/projects.dto';
import { MultiSurveyReportPayloadDto } from './dtos/reports.dto';
import { BulkStatsMapper } from './mapper/bulk-stats.mapper';
import { SurveyMapper } from './mapper/survey.mapper';
import { ApiStatusMessageResponse } from '@ps/utils';
import { withLoading } from '@ps/core-loading';

@Injectable({ providedIn: 'root' })
export class ProjectApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = this._env.pureSpectrum.url;
  private readonly _reportServiceUrl = this._env.pureSpectrum.reportServiceUrl;

  private readonly _bulkStatsMapper = new BulkStatsMapper();
  private readonly _surveyMapper = new SurveyMapper();

  public getSurveyBulkStats(surveyIds: number[]): Observable<BulkStats> {
    return this._http
      .post<BulkStatsDto>(
        `${this._baseUrl}/buyers/v2/projects/stats`,
        {
          surveyIds,
        },
        { context: withLoading('page') },
      )
      .pipe(map((response) => this._bulkStatsMapper.fromDto(response)));
  }

  public createMcGroupedProject(
    input: ProjectPayloadInput,
  ): Observable<Survey> {
    return this._createProject(
      new McGroupedUpdatePayloadMapper().toCreatePayload(input),
    );
  }

  // `targeting=0` only shapes the response — it keeps the rehydrated quotas and
  // qualifications out of the body we discard anyway; it never gates the write.
  public updateMcGroupedProject(
    projectId: number,
    input: ProjectUpdatePayloadInput,
  ): Observable<Survey> {
    return this._http
      .put<CreatedProjectDto>(
        `${this._baseUrl}/buyers/v2/projects/${projectId}?UI=1&targeting=0`,
        new McGroupedUpdatePayloadMapper().toUpdatePayload(input),
        { context: withLoading('page') },
      )
      .pipe(map((dto) => this._surveyMapper.fromDetailsDto(dto)));
  }

  public getCombinedSummaryReport(
    email: string,
    selectedSurveys: Survey[],
  ): Observable<ApiStatusMessageResponse> {
    const body: MultiSurveyReportPayloadDto = {
      email,
      surveys: selectedSurveys.map((survey) => survey.id),
    };
    return this._http.post<ApiStatusMessageResponse>(
      `${this._reportServiceUrl}/multi-surveys`,
      body,
    );
  }

  private _createProject(payload: ProjectPayloadDto): Observable<Survey> {
    return this._http
      .post<CreatedProjectDto>(
        `${this._baseUrl}/buyers/v2/projects?UI=1`,
        payload,
        { context: withLoading('page') },
      )
      .pipe(map((dto) => this._surveyMapper.fromDetailsDto(dto)));
  }
}
